import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import conductor from 'scoreboard/components/conductor'
import appDisplay from 'scoreboard/components/app-display'
import i18n from 'scoreboard/components/i18n'
import deleteDomainObject from 'scoreboard/components/edit/delete-domain-object'
import SaveReportForm from './save-report-form'
import template from '../components/save-controls.hbs'
import EditButtons from 'scoreboard/components/edit/edit-buttons'
import permissions from 'scoreboard/components/permissions'
import Dataset from 'scoreboard/datasets/model/dataset'

/**
 * we are copying the current model, so we need to delete the thresholdcoloring
 * ids so they are saved as new
 */
const duplicateThresholdColorings = ({ structure }) => {
	_.each(structure?.stacks, s => {
		_.each(s?.columns, c => {
			if (!_.isEmpty(c?.thresholdColoring)) delete c.thresholdColoring.id
		})
	})
}

function showNewForm(model) {
	var nav = appDisplay.navView()

	model = model.cloneAsNew()
	let structure = _.cloneDeep(model.get('structure'))
	duplicateThresholdColorings({ structure })
	model.set('structure', structure)
	model.set('icon', 'reports')

	var form = new SaveReportForm({
		model,
		title: i18n.get('report.common.new')
	})

	var def = observePersistence(form, form.model)
	appDisplay.dialog(form)

	var parentId = form.model.get('parentId') || conductor.id()
	if (nav.showingTree) {
		if (!model.isNew()) {
			//for new reports, the placeholder is added immediately, for existing
			//reports, if they want to clone we then show the placeholder
			var parentId = parentId
			nav.addTransient(parentId, form.model)
			form.once('cancel', nav.removeTransient.bind(nav))
		}
		form.on('change', nav.changeTransient.bind(nav))
	} else {
		//ensure index is set correctly on first report in tree
		form.model.set(nav.gardener.getAddPosition(form.model, parentId), {silent: true})
	}
	return def
}

function observePersistence(view, model) {
	var def = new $.Deferred()
	var parentId = model.get('parentId') || conductor.id()

	if (model.isNew()) {
		view.once('done', model => {
			//#20062 ensure the promise chain isn't broken as the view shown may have been
			//initiated by a save guard
			def.then(() => {
				Backbone.trigger('add:domain-object', model, parentId)
				observePersistence(view, model)
			})
		})
		view.once('cancel', def.resolve.bind(view, 'cancelFromNewSave'))
	} else {
		view.listenTo(model, 'sync', function() {
			Backbone.trigger('edit:domain-object', model, parentId)
		})
		view.once('cancel', def.resolve.bind(view))
	}
	view.once('done', def.resolve.bind(view))
	return def
}

var SaveControls = Backbone.View.extend({

	// this.model is the Report
	tagName: 'section',

	className: 'table-hack-row',

	events: {
		'click .move': 'move',
		'click .copy': 'copy',
		'click .save-as-new' : 'saveAsNew',
		'click .delete' : 'delete'
		//note the cancel and submit events are being listened by a FormView
	},

	initialize: function(options) {
		this.opts = options || {}
		var action = this.model.isNew() ? 'add' : 'edit'
		this.listenTo(this.model, 'change', this.updateButtons.bind(this))
		observePersistence(this, this.model)
		this.editButtons = new EditButtons(options)

		if (this.model.get('subtype') === 'dataset-report-designer') {
			const dataset = this.model.get('dataset')
			this.listenTo(dataset, 'change:status', this.updateButtons.bind(this))
		}
	},

	render: function() {
		this.$el.toggleClass('is-not-displayed', true)

		var msgCode = this.model.isNew() ? 'report.new.unsaved' : 'report.modified.unsaved'
		var content = $(template({ message: msgCode }))

		// set display to table since we're dealing with .table-hack-row
		this.$el.html(content).css('display','table')
		this.updateButtons()

		return this
	},

	isDatasetError: function() {
		if (this.model.get('subtype') === 'dataset-report-designer') {
			const dataset = this.model.get('dataset')
			const status = dataset.get('status')
			return status === Dataset.ERROR
		}

		return false
	},

	updateButtons: function() {
		var inEditForm = this.opts.editForm
		var isNew = this.model.isNew()
		var isDirty = this.model.get('dirty') || isNew
		var err = this.model.validate && this.model.validate(this.model.attributes) || {}
		var isValid = _.isEmpty(err) && !this.isDatasetError()
		let spectating = this.opts.spectating || false

		var content = this.$el
		//determine the save controls to show, #8283
		//initial state can save, then check various conditions that would prevent a save from showing
		content.find('.save-as-new, .save').toggleClass('is-not-displayed', false)

		// Move, Copy, Delete: Only shown on the edit tab for reports that have been saved before.
		content.find('.move-copy-delete').toggleClass('is-not-displayed', !inEditForm || isNew)

		// Unsaved message(s): Only shown on the overview tab when the form is dirty.
		content.find('.save-controls-message').toggleClass('is-not-displayed', inEditForm || !isDirty)

		//Cancel: only show when report has been saved before
		//Always show on the edit tab, only show overview when form dirty
		content.find('.cancel').toggleClass('is-not-displayed', !isDirty || isNew)

		//#14517 don't allow cloning when spectating
		//#15121 don't allow cloning when new
		content.find('.save-as-new').toggleClass('is-not-displayed', spectating || isNew)

		//allow save as new if everything except report label passed validation
		// save as new dialog will handle setting label
		content.find('.save-as-new').toggleClass('is-disabled', (!isValid && !err.label) || !isDirty)

		//#27796 if in briefing hide everything
		if (this.opts.spectating) {
			this.$('.edit-buttons-bottom').children().addClass('is-not-displayed')
		}

		// do not show if on overview if report is not dirty or not new
		var hide = (!inEditForm && !isNew && !isDirty) && !this.isDatasetError()
		this.$el.toggleClass('is-not-displayed', hide)
	},

	cancel: function(e) {
		if (e && $(e.target).hasClass('is-disabled') || !this.model.get('dirty')) return

		if (this.model.cancelAttributes) {
			//don't trigger any of the built in event mechanism here since we're
			//handle the necessary logic for this particular scenario here
			this.model.reset(this.model.cancelAttributes)
		}

		if (!this.opts.editForm && !this.model.isNew()) {
			Backbone.history.loadUrl()
		}
	},

	//returns true if an model failed validation
	test: function() {
		if (!permissions.reports(this.model.get('organizationId'))) {
			return i18n.get('report.noPermissionToSave')
		}
		return _.invoke(this.model, 'validate', this.model.attributes, {messageList: true})
	},

	allowCleanSubmission: function() {
		var isNew = this.model.isNew()
		var isDirty = this.model.get('dirty') || isNew
		return (isNew || isDirty) && !this.test()
	},

	saveAsNew: function(e) {
		if (e && $(e.target).hasClass('is-disabled')) return

		this.trigger('save-as-new')
		return showNewForm(this.model)
	},

	submit: function(e) {
		if (this.model.isNew()) {
			var inEditForm = this.opts.editForm
			if (!inEditForm) {
				//we're on overview, show the new report dialog
				return this.saveAsNew(e)
			} else if (!_.isEmpty(this.model.get('label'))) {
				return this.model.save(null, this.opts)
			}
		} else if (this.model.get('dirty')) {
			return this.model.save(null, _.defaults({persist:true}, this.opts))
		}
	},

	delete: function(e) {
		deleteDomainObject(this)
	},

	move: function(e) {
		this.editButtons.move()
	},

	copy: function(e) {
		this.editButtons.copy()
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.editButtons], 'remove')
	}

})

_.extend(SaveControls, {
	duplicateThresholdColorings
})
export default SaveControls
export { duplicateThresholdColorings }
SaveControls.__filename__ = 'reports/save-controls.js'
